/**
 * WifiDashboard — /wifi/dashboard (resumen del WiFi).
 *
 * Panel de un vistazo: cuánta infraestructura hay, cómo está repartida y qué
 * necesita atención (APs sueltos, SSIDs abiertos, RADIUS sin uso). Sigue la
 * estética del estándar de listados:
 *  · misma cabecera (`.list-head`) y mismos chips de KPI que las listas, cada uno
 *    llevando a su pantalla;
 *  · iconos de `@heroicons/react` (los del menú de WiFi), ni un emoji;
 *  · colores solo con las variables del sistema de diseño.
 *
 * No hay fila de botones de acceso rápido: para eso está el menú lateral. Los
 * números sí son clicables y cada tarjeta tiene su «Ver…» que lleva al listado.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  WifiIcon,
  SignalIcon,
  RectangleGroupIcon,
  Square3Stack3DIcon,
  LockClosedIcon,
  MapIcon,
  BoltIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  planned:     { label: 'Planificado',   badge: 'list-badge' },
  decom:       { label: 'Decom',         badge: 'list-badge--off' },
};

/** Color de una barra según lo lleno que esté (o lo grave que sea). */
function tone(pct) {
  if (pct >= 90) return 'var(--red-600)';
  if (pct >= 70) return 'var(--amber-600)';
  return 'var(--green-600)';
}

/** Barra de progreso del estándar (la misma que usan las columnas de las listas). */
function Bar({ pct, width = 90, color }) {
  const value = Math.max(0, Math.min(100, pct || 0));
  return (
    <span style={{
      display: 'inline-block', width, height: 7, borderRadius: 4,
      background: 'var(--bg-toolbar)', overflow: 'hidden', flexShrink: 0,
    }}>
      <span style={{ display: 'block', width: `${value}%`, height: '100%', background: color || tone(value) }} />
    </span>
  );
}

const cardStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md, 10px)',
};

const cardHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)',
};

export default function WifiDashboard({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();

  const [controllers, setControllers] = useState([]);
  const [aps, setAps] = useState([]);
  const [ssids, setSsids] = useState([]);
  const [radiusServers, setRadiusServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [ctrlRes, apRes, ssidRes, rsRes] = await Promise.all([
        api.getWifiControllers(companyId),
        api.getWifiAPs(companyId),
        api.getWifiSSIDs(companyId).catch(() => ({ data: [] })),
        api.getWifiRadiusServers(companyId),
      ]);
      setControllers(ctrlRes.data || []);
      setAps(apRes.data || []);
      setSsids(ssidRes.data || []);
      setRadiusServers(rsRes.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando el resumen del WiFi');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ── Cuentas ────────────────────────────────────────────────────────────── */
  const k = useMemo(() => {
    const byStatus = aps.reduce((acc, a) => {
      const s = a.status || 'active';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    const standalone = aps.filter(a => !a.controller_id);
    const managed = aps.filter(a => a.controller_id);
    const noLocation = aps.filter(a => !a.location_id).length;
    const byBand = aps.reduce((acc, a) => {
      if (!a.band) return acc;
      acc[a.band] = (acc[a.band] || 0) + 1;
      return acc;
    }, {});
    const byStandard = aps.reduce((acc, a) => {
      const s = (a.wifi_standard || '').toUpperCase();
      if (!s) return acc;
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});

    const bySecurity = ssids.reduce((acc, s) => {
      const mode = s.security_mode || 'desconocido';
      acc[mode] = (acc[mode] || 0) + 1;
      return acc;
    }, {});
    const openSsids = ssids.filter(s => s.security_mode === 'open');
    const enterpriseCount = ssids.filter(s => (s.security_mode || '').includes('enterprise')).length;
    const withRadius = ssids.filter(s => s.radius_server_id).length;

    const radiusActive = radiusServers.filter(r => (r.status || 'active') === 'active').length;
    const radiusUnused = radiusServers.filter(r => !(r.ssid_count > 0)).length;

    /* Controladores ordenados por APs gestionados (el que más tiene, primero). */
    const topControllers = controllers
      .map(c => ({ ...c, _managed: aps.filter(a => a.controller_id === c.id).length }))
      .sort((a, b) => b._managed - a._managed);

    return {
      byStatus, standalone, managed, noLocation, byBand, byStandard,
      bySecurity, openSsids, enterpriseCount, withRadius,
      radiusActive, radiusUnused, topControllers,
    };
  }, [aps, ssids, controllers, radiusServers]);

  const kpiChips = [
    { key: 'ctrl', icon: SignalIcon, label: tr('wifi.controllers_plural', 'Controladores'), value: controllers.length, path: '/wifi/controllers' },
    { key: 'aps', icon: RectangleGroupIcon, label: tr('wifi.aps_title', 'Puntos de acceso'), value: aps.length, path: '/wifi/aps' },
    { key: 'standalone', icon: WifiIcon, label: tr('wifi.standalone_plural', 'APs standalone'), value: k.standalone.length, path: '/wifi/aps?controllerId=none' },
    { key: 'ssids', icon: Square3Stack3DIcon, label: 'SSIDs', value: ssids.length, path: '/wifi/topology' },
    { key: 'radius', icon: LockClosedIcon, label: tr('radius.plural', 'Servidores RADIUS'), value: radiusServers.length, path: '/wifi/radius-servers' },
  ];

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('wifi.select_company', 'Selecciona una empresa para ver el WiFi.')}
        </div>
      </div>
    );
  }

  const maxBand = Math.max(1, ...Object.values(k.byBand));

  return (
    <div className="list-page" style={{ padding: '1.5rem' }}>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* ══ Cabecera, igual que la de una lista ══ */}
      <div className="list-head">
        <div className="list-head-left">
          <WifiIcon className="list-head-icon" />
          <div>
            <h1 className="list-title">{tr('wifi.dash_title', 'WiFi')}</h1>
            <p className="list-subtitle">
              {loading
                ? tr('msg.loading', 'Cargando…')
                : `${controllers.length} ${tr('wifi.controllers_plural', 'controladores')}`
                  + ` · ${aps.length} ${tr('wifi.ap_plural', 'puntos de acceso')}`
                  + ` · ${ssids.length} SSIDs`}
            </p>
          </div>
        </div>
        <div className="list-head-actions">
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => navigate('/wifi/topology')}
            title={tr('wifi.view_topology', 'Ver la topología WiFi')}
          >
            <Square3Stack3DIcon style={{ width: 15, height: 15 }} /> {tr('wifi.topology', 'Topología')}
          </button>
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => navigate('/wifi/map')}
            title={tr('wifi.view_map', 'Ver el plano de planta')}
          >
            <MapIcon style={{ width: 15, height: 15 }} /> {tr('wifi.map', 'Plano')}
          </button>
        </div>
      </div>

      {/* ══ Chips de KPI: cada uno lleva a su pantalla ══ */}
      <div className="list-kpis list-kpis--chips" style={{ marginBottom: 16 }}>
        {kpiChips.map(chip => {
          const Icon = chip.icon;
          return (
            <button
              key={chip.key}
              type="button"
              className="list-kpi-chip"
              onClick={() => navigate(chip.path)}
              title={`${tr('dash.go_to', 'Ir a')} ${chip.label}`}
            >
              <span className="list-kpi-value">{chip.value}</span>
              <span className="list-kpi-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Icon style={{ width: 13, height: 13 }} />
                {chip.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* ══ Aviso: SSIDs abiertos ══ */}
      {k.openSsids.length > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            border: '1px solid var(--border)', borderLeft: '3px solid var(--amber-600)',
            background: 'var(--bg-card)', borderRadius: 'var(--radius-md, 10px)',
            padding: '10px 14px', marginBottom: 16,
          }}
        >
          <ShieldExclamationIcon style={{ width: 18, height: 18, color: 'var(--amber-600)', flexShrink: 0 }} />
          <span>
            {tr('wifi.open_ssids_warn', 'Hay SSIDs sin seguridad:')}{' '}
            <strong>{k.openSsids.map(s => s.name).join(', ')}</strong>
          </span>
        </div>
      )}

      <div className="row g-3">
        {/* ══ Estado de los APs ══ */}
        <div className="col-lg-4">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <RectangleGroupIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('wifi.ap_status', 'Estado de los APs')}
              </strong>
              <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/wifi/aps')}>
                {tr('dash.see_all', 'Ver todas')} →
              </button>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {aps.length === 0 && <span className="list-hint">{tr('wifi.ap_empty', 'No hay puntos de acceso')}</span>}
              {aps.length > 0 && Object.entries(STATUS_META).map(([status, meta]) => {
                const count = k.byStatus[status] || 0;
                if (!count) return null;
                const pct = Math.round((count / aps.length) * 100);
                return (
                  <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <span className={`list-badge ${meta.badge}`} style={{ minWidth: 96 }}>{meta.label}</span>
                    <Bar pct={pct} width={70} />
                    <span className="list-hint" style={{ marginLeft: 'auto' }}>{count} · {pct}%</span>
                  </div>
                );
              })}
              {k.noLocation > 0 && (
                <div className="list-hint" style={{ marginTop: 8 }}>
                  {k.noLocation} {tr('wifi.no_location_plural', 'APs sin ubicación asignada')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Bandas y estándares ══ */}
        <div className="col-lg-4">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SignalIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('wifi.bands', 'Bandas y estándares')}
              </strong>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {Object.keys(k.byBand).length === 0 && (
                <span className="list-hint">{tr('wifi.no_bands', 'Ningún AP tiene banda declarada.')}</span>
              )}
              {Object.entries(k.byBand).sort((a, b) => b[1] - a[1]).map(([band, count]) => (
                <div key={band} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span className="list-code" style={{ minWidth: 70 }}>{band}</span>
                  <Bar pct={(count / maxBand) * 100} width={90} />
                  <span className="list-hint" style={{ marginLeft: 'auto' }}>{count}</span>
                </div>
              ))}

              {Object.keys(k.byStandard).length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {Object.entries(k.byStandard).sort((a, b) => b[1] - a[1]).map(([std, count]) => (
                    <span key={std} className="list-badge">{std} · {count}</span>
                  ))}
                </div>
              )}

              <div className="list-hint" style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <BoltIcon style={{ width: 14, height: 14 }} />
                {aps.filter(a => a.poe_powered).length} {tr('wifi.poe_count', 'APs alimentados por PoE')}
              </div>
            </div>
          </div>
        </div>

        {/* ══ Seguridad de los SSIDs ══ */}
        <div className="col-lg-4">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ShieldExclamationIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('wifi.ssid_security', 'Seguridad de los SSIDs')}
              </strong>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {ssids.length === 0 && <span className="list-hint">{tr('wifi.no_ssids', 'No hay SSIDs definidos.')}</span>}
              {ssids.length > 0 && Object.entries(k.bySecurity).sort((a, b) => b[1] - a[1]).map(([mode, count]) => (
                <div key={mode} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span
                    className={`list-badge ${mode === 'open' ? 'list-badge--off' : mode.includes('enterprise') ? 'list-badge--on' : ''}`.trim()}
                    style={{ minWidth: 140 }}
                  >
                    {mode.replace(/_/g, ' ')}
                  </span>
                  <span className="list-hint" style={{ marginLeft: 'auto' }}>{count}</span>
                </div>
              ))}
              {ssids.length > 0 && (
                <div className="list-hint" style={{ marginTop: 8 }}>
                  {k.enterpriseCount} {tr('wifi.enterprise_count', 'con autenticación enterprise')}
                  {k.withRadius > 0 && <> · {k.withRadius} {tr('wifi.with_radius', 'con RADIUS enlazado')}</>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Servidores RADIUS ══ */}
        <div className="col-lg-5">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <LockClosedIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('radius.title', 'Servidores RADIUS')}
              </strong>
              <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/wifi/radius-servers')}>
                {tr('dash.see_all', 'Ver todas')} →
              </button>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {radiusServers.length === 0 && (
                <span className="list-hint">{tr('radius.empty', 'No hay servidores RADIUS')}</span>
              )}
              {radiusServers.length > 0 && (
                <div className="list-hint" style={{ marginBottom: 8 }}>
                  {k.radiusActive} {tr('wifi.radius_active', 'activos')}
                  {k.radiusUnused > 0 && <> · {k.radiusUnused} {tr('wifi.radius_unused', 'sin ningún SSID que los use')}</>}
                </div>
              )}
              {radiusServers.map(rs => {
                const meta = STATUS_META[rs.status] || STATUS_META.active;
                return (
                  <div
                    key={rs.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/wifi/radius-servers?destacar=${rs.id}&abrir=1`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/wifi/radius-servers?destacar=${rs.id}&abrir=1`); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer' }}
                    title={`${tr('dash.go_to', 'Ir a')} ${rs.name}`}
                  >
                    <span style={{ fontWeight: 600, minWidth: 120 }}>{rs.name}</span>
                    <span className="list-code">{rs.host}</span>
                    <span className={`list-badge ${meta.badge}`} style={{ marginLeft: 'auto' }}>{meta.label}</span>
                    <span className="list-badge">{rs.ssid_count || 0} SSIDs</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ══ Infraestructura: controlador → APs ══ */}
        <div className="col-lg-7">
          <div style={{ ...cardStyle, height: '100%' }}>
            <div style={cardHeader}>
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SignalIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                {tr('wifi.infrastructure', 'Infraestructura')}
              </strong>
              <span className="list-hint">
                {k.managed.length} {tr('wifi.managed_count', 'APs gestionados')}
                {k.standalone.length > 0 && <> · {k.standalone.length} standalone</>}
              </span>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {controllers.length === 0 && k.standalone.length === 0 && (
                <span className="list-hint">
                  {tr('wifi.no_infra', 'Sin infraestructura WiFi configurada todavía.')}
                </span>
              )}

              {k.topControllers.map(c => {
                const meta = STATUS_META[c.status] || STATUS_META.active;
                const pct = c.max_aps ? Math.min(100, Math.round((c._managed / c.max_aps) * 100)) : null;
                return (
                  <div key={c.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        type="button"
                        className="list-badge"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/wifi/aps?controllerId=${c.id}`)}
                        title={tr('wifi.view_aps', 'Ver los APs de este controlador')}
                      >
                        {c.name}
                      </button>
                      <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
                      <span className="list-hint" style={{ marginLeft: 'auto' }}>
                        {c._managed}{c.max_aps ? ` / ${c.max_aps}` : ''} APs
                        {pct != null && <> · {pct}%</>}
                      </span>
                    </div>
                    {(c.managed_domains_label || c.management_ip) && (
                      <div className="list-hint" style={{ paddingLeft: 2 }}>
                        {[c.managed_domains_label, c.management_ip].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })}

              {k.standalone.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div className="list-hint" style={{ marginBottom: 4 }}>
                    {tr('wifi.standalone_hint', 'APs sin controlador (gestionan sus propios SSIDs)')}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {k.standalone.map(ap => (
                      <button
                        key={ap.id}
                        type="button"
                        className="list-badge"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/wifi/aps?destacar=${ap.id}&abrir=1`)}
                        title={`${tr('dash.go_to', 'Ir a')} ${ap.name}`}
                      >
                        {ap.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
